// ============================================================================
// TOURNAMENT SERVICE
// Handles all tournament-related API calls
// ============================================================================

const API_BASE_URL = 'https://api.fsgu.co.za';

/**
 * Create a new tournament
 * @param {Object} tournamentData - Tournament data
 * @param {number} adminId - Admin user ID creating the tournament
 * @returns {Promise<Object>} Created tournament data
 */
export const createTournament = async (tournamentData, adminId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/create-tournament.php`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        ...tournamentData,
        created_by: adminId
      })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to create tournament');
    }

    return data;
  } catch (error) {
    console.error('Error creating tournament:', error);
    throw error;
  }
};

/**
 * Update an existing tournament
 * @param {number} tournamentId - Tournament ID
 * @param {Object} updates - Fields to update
 * @param {number} adminId - Admin user ID updating the tournament
 * @returns {Promise<Object>} Updated tournament data
 */
export const updateTournament = async (tournamentId, updates, adminId) => {
  try {
    console.log('Updating tournament with category:', updates.category);
    
    const response = await fetch(`${API_BASE_URL}/update-tournament.php`, {
      method: 'POST', // Changed from PUT to POST for better CORS support
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: tournamentId,
        ...updates,
        updated_by: adminId
      })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      console.error('Backend error response:', data);
      throw new Error(data.message || 'Failed to update tournament');
    }

    return data;
  } catch (error) {
    console.error('Error updating tournament:', error);
    throw error;
  }
};

/**
 * Delete a tournament
 * @param {number} tournamentId - Tournament ID to delete
 * @param {number} adminId - Admin user ID deleting the tournament
 * @returns {Promise<Object>} Delete response
 */
export const deleteTournament = async (tournamentId, adminId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/delete-tournament.php`, {
      method: 'POST', // Changed from DELETE to POST for better CORS support
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        id: tournamentId,
        deleted_by: adminId
      })
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to delete tournament');
    }

    return data;
  } catch (error) {
    console.error('Error deleting tournament:', error);
    throw error;
  }
};

/**
 * Get all tournaments with optional filters
 * @param {Object} filters - Filter options
 * @returns {Promise<Object>} Tournaments data
 */
export const getTournaments = async (filters = {}) => {
  try {
    const params = new URLSearchParams();
    
    if (filters.category && filters.category !== 'all') {
      params.append('category', filters.category);
    }
    
    if (filters.status && filters.status !== 'all') {
      params.append('status', filters.status);
    }
    
    if (filters.featured) {
      params.append('featured', 'true');
    }
    
    if (filters.upcoming) {
      params.append('upcoming', 'true');
    }
    
    if (filters.year) {
      params.append('year', filters.year);
    }
    
    if (filters.limit) {
      params.append('limit', filters.limit);
    }
    
    if (filters.offset) {
      params.append('offset', filters.offset);
    }

    const url = `${API_BASE_URL}/get-tournaments.php${params.toString() ? '?' + params.toString() : ''}`;
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to fetch tournaments');
    }

    return data;
  } catch (error) {
    console.error('Error fetching tournaments:', error);
    throw error;
  }
};

/**
 * Get a single tournament by ID
 * @param {number} tournamentId - Tournament ID
 * @returns {Promise<Object>} Tournament data
 */
export const getTournament = async (tournamentId) => {
  try {
    const response = await fetch(`${API_BASE_URL}/get-tournament.php?id=${tournamentId}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      }
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || 'Failed to fetch tournament');
    }

    return data;
  } catch (error) {
    console.error('Error fetching tournament:', error);
    throw error;
  }
};

/**
 * Get featured tournaments for homepage
 * @param {number} limit - Number of tournaments to fetch
 * @returns {Promise<Object>} Featured tournaments
 */
export const getFeaturedTournaments = async (limit = 3) => {
  return getTournaments({
    featured: true,
    upcoming: true,
    limit
  });
};

/**
 * Get upcoming tournaments
 * @param {Object} filters - Additional filters
 * @returns {Promise<Object>} Upcoming tournaments
 */
export const getUpcomingTournaments = async (filters = {}) => {
  return getTournaments({
    ...filters,
    upcoming: true
  });
};

/**
 * Get tournaments by category
 * @param {string} category - Category (Men, Women, Juniors, Senior, Open)
 * @param {Object} additionalFilters - Additional filters
 * @returns {Promise<Object>} Tournaments in category
 */
export const getTournamentsByCategory = async (category, additionalFilters = {}) => {
  return getTournaments({
    ...additionalFilters,
    category
  });
};

/**
 * Get tournaments by year
 * @param {number} year - Year
 * @param {Object} additionalFilters - Additional filters
 * @returns {Promise<Object>} Tournaments in year
 */
export const getTournamentsByYear = async (year, additionalFilters = {}) => {
  return getTournaments({
    ...additionalFilters,
    year
  });
};

/**
 * Change tournament status
 * @param {number} tournamentId - Tournament ID
 * @param {string} newStatus - New status
 * @param {number} adminId - Admin user ID
 * @returns {Promise<Object>} Update response
 */
export const changeTournamentStatus = async (tournamentId, newStatus, adminId) => {
  return updateTournament(tournamentId, { status: newStatus }, adminId);
};

/**
 * Toggle tournament featured status
 * @param {number} tournamentId - Tournament ID
 * @param {boolean} isFeatured - New featured status
 * @param {number} adminId - Admin user ID
 * @returns {Promise<Object>} Update response
 */
export const toggleFeatured = async (tournamentId, isFeatured, adminId) => {
  return updateTournament(tournamentId, { is_featured: isFeatured }, adminId);
};

/**
 * Update tournament images
 * @param {number} tournamentId - Tournament ID
 * @param {Object} images - Image URLs and paths
 * @param {number} adminId - Admin user ID
 * @returns {Promise<Object>} Update response
 */
export const updateTournamentImages = async (tournamentId, images, adminId) => {
  return updateTournament(tournamentId, images, adminId);
};